import React, {useEffect, useMemo, useState} from 'react';
import {CartItemDataHook} from '../../CartItem';
import {CounterSize} from '@wix/wix-ui-tpa';
import {Counter, Popover} from '@wix/wix-ui-tpa/cssVars';
import {debounce} from '../../../../../../domain/lib/debounce';
import {getMaxItemQuantity, getIsQuantityErrorNeeded, getIsOutOfStock} from '../../../../../../domain/utils/itemUtils';
import {useControllerProps} from '../../../../../../domain/controllers/ControllerContext';
import {useTranslation, useExperiments} from '@wix/yoshi-flow-editor';
import {classes} from './Quantity.st.css';
import classNames from 'clsx';
import {LineItemModel} from '../../../../../../domain/models/LineItems/LineItem.model';
import {SPECS} from '../../../../../../domain/specs';

export const Quantity = ({lineItem, className}: {lineItem: LineItemModel; className?: string}) => {
  const {t} = useTranslation();
  const {experiments} = useExperiments();
  const fixDebouncedUpdateItemQuantity = experiments.enabled(SPECS.FixDebouncedUpdateItemQuantity);
  const {updateItemQuantity, shouldPresentTooltipWithoutNumber} = useControllerProps().cartStore;
  const debouncedUpdateItemQuantityNew = useMemo(
    () => debounce((lineItemId: string, quantity: number) => updateItemQuantity(lineItemId, quantity)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const debouncedUpdateItemQuantityOld = useMemo(() => debounce(updateItemQuantity), [updateItemQuantity]);
  const debouncedUpdateItemQuantity = fixDebouncedUpdateItemQuantity
    ? debouncedUpdateItemQuantityNew
    : debouncedUpdateItemQuantityOld;

  const [quantity, setQuantity] = useState<number>(lineItem.quantityInfo.quantity);
  const [hasQuantityError, sethasQuantityError] = useState<boolean>(false);
  const handleChange = (value: string) => {
    const updatedValue = +value || 1;

    const isQuantityErrorNeeded = getIsQuantityErrorNeeded(lineItem.quantityInfo.availableQuantity, updatedValue);

    if (updatedValue === quantity) {
      return;
    }

    if (isQuantityErrorNeeded) {
      triggerQuantityErrorIfNeeded();
      return;
    }

    setQuantity(updatedValue);
    void debouncedUpdateItemQuantity(lineItem.id, updatedValue);
  };

  const triggerQuantityErrorIfNeeded = () => {
    sethasQuantityError(true);
    setTimeout(() => {
      sethasQuantityError(false);
    }, 2e3);
  };
  useEffect(
    () => {
      setQuantity(lineItem.quantityInfo.quantity);
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ [lineItem.quantityInfo.quantity]
  );

  return (
    <Popover
      data-hook={CartItemDataHook.QuantityErrorTooltip}
      className={classNames(classes.popover, className)}
      width={165}
      shown={hasQuantityError}
      placement="top">
      <Popover.Element>
        <Counter
          disabled={getIsOutOfStock(lineItem.quantityInfo.availableQuantity)}
          className={classes.quantity}
          size={CounterSize.xSmall}
          error={hasQuantityError}
          value={quantity}
          step={1}
          data-hook={CartItemDataHook.Quantity}
          onChange={handleChange}
          inputAriaLabel={t('cart.sr.chooseQty')}
          aria-label={t('cart.sr.quantity')}
          min={1}
          max={getMaxItemQuantity(lineItem.quantityInfo.availableQuantity)}
        />
      </Popover.Element>
      <Popover.Content>
        <span role="alert">
          {shouldPresentTooltipWithoutNumber
            ? t('cart.errors.item_quantity_exceed_inventory_no_number')
            : t('cart.errors.item_quantity_exceed_inventory', {
                itemsAmount: getMaxItemQuantity(lineItem.quantityInfo.availableQuantity),
              } as object)}
        </span>
      </Popover.Content>
    </Popover>
  );
};
